import { selectAIRemainingRequests } from '@store/reducers/ai/selectors'
import type {
  AiRequest,
  AiResponse,
  AiTask,
  AiTaskCreateResponse,
} from '@store/reducers/ai/types'
import { artBoardActions } from '@store/reducers/artboard'
import { rollbackToBackupSnapshot } from '@storeOld/actionCreators'
import axios, { type AxiosResponse, type CancelTokenSource } from 'axios'
import type { SagaIterator } from 'redux-saga'
import { call, delay, put, race, select } from 'redux-saga/effects'

import { GtmTriggerName, sendGtmEvent } from '@/constants/gtm'
import { AIExternalActions, AIInternalActions } from '../actions'
import { handleAIResponse } from './handleResponse'
import { performAIRequest } from './performRequest'
import { prepareAIRequestData } from './prepareRequest'
import {
  AI_TASK_COMPLETION_TIMEOUT_MS,
  classifyAIChatError,
  createTaskCompletionTimeoutError,
  getTaskFromResponse,
  isAiResponse,
  isLimitReachedError,
  isTaskInProgressError,
  resolveTaskFromConflict,
  waitForTaskCompletion,
} from './taskFlow'

function* reportSendMessageFailure(
  error: unknown,
  actions: any[]
): SagaIterator {
  console.error('AI request failed:', error)

  if (actions.length) {
    yield put(rollbackToBackupSnapshot())
  }

  if (!axios.isCancel(error)) {
    sendGtmEvent(GtmTriggerName.AI_CHAT_RESPONSE_ERROR)
    yield put(AIInternalActions.setError(classifyAIChatError(error)))
  }
}

function* handleTaskCompletion(task: AiTask): SagaIterator<any[]> {
  const { completionResponse, timedOut } = yield race({
    completionResponse: call(waitForTaskCompletion, task),
    timedOut: delay(AI_TASK_COMPLETION_TIMEOUT_MS),
  })

  if (timedOut) {
    throw createTaskCompletionTimeoutError()
  }

  if (!completionResponse) {
    return []
  }

  const actions: any[] = yield call(
    handleAIResponse,
    completionResponse as AiResponse,
    { skipProjectActions: true }
  )
  return actions
}

export function* sendMessage({
  payload: { message },
}: ReturnType<typeof AIExternalActions.sendMessage>): SagaIterator {
  let actions: any[] = []
  let cancelTokenSource: CancelTokenSource | null = null
  let requestData: AiRequest | null = null
  const userMessageAdded = Boolean(message)

  const remainingRequests = yield select(selectAIRemainingRequests)
  if (remainingRequests === 0) {
    return
  }

  try {
    cancelTokenSource = axios.CancelToken.source()

    yield put(AIInternalActions.initGeneration({ cancelTokenSource }))
    yield put(artBoardActions.setAiGeneratingStatus({ status: true }))

    sendGtmEvent(GtmTriggerName.AI_CHAT_PROMPT_SEND)

    if (message) {
      yield put(
        AIExternalActions.addMessage({
          message: {
            type: 'user',
            text: message,
          },
        })
      )
    }

    const preparedRequestData: AiRequest = yield call(prepareAIRequestData)
    requestData = preparedRequestData

    const response: AxiosResponse<AiResponse | AiTaskCreateResponse> =
      yield call(performAIRequest, preparedRequestData, cancelTokenSource.token)

    if (isAiResponse(response.data)) {
      actions = yield call(handleAIResponse, response.data)
      return
    }

    const task = getTaskFromResponse(response.data)
    if (!task) {
      throw new Error('Unexpected AI chat response format')
    }

    actions = yield call(handleTaskCompletion, task)
  } catch (error) {
    if (isLimitReachedError(error)) {
      if (userMessageAdded) {
        yield put(AIInternalActions.removeLastMessage())
      }
      yield put(AIInternalActions.updateRemainingRequests(0))
      return
    }

    if (isTaskInProgressError(error) && requestData && cancelTokenSource) {
      if (userMessageAdded) {
        yield put(AIInternalActions.removeLastMessage())
      }

      try {
        const activeTask: AiTask | null = yield call(
          resolveTaskFromConflict,
          requestData,
          cancelTokenSource.token
        )

        if (activeTask) {
          actions = yield call(handleTaskCompletion, activeTask)
          return
        }
      } catch (reconnectError) {
        yield call(reportSendMessageFailure, reconnectError, actions)
        return
      }
    }

    yield call(reportSendMessageFailure, error, actions)
  } finally {
    yield put(artBoardActions.setAiGeneratingStatus({ status: false }))
    yield put(AIInternalActions.resetGeneration())
  }
}
